import { useEffect, useMemo, useState } from "react";
import useTranslator from "../../../hooks/useTranslator";
import { VscEye, VscEyeClosed } from "react-icons/vsc";
import Icon from "../Icon/Icon";
import TextField from "../TextField/TextField";
import "./PasswordInput.css";

export interface PasswordInputProps {
  name: string;
  value: string;
  label?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  inputError?: string;
  setInputError?: (error: string) => void;
  isShowChecks?: boolean;
}

/**
 * Password field with show / hide and, optionally, the live list of
 * password rules (isShowChecks).
 * Example: <PasswordInput name="password" value={v} onChange={fn} isShowChecks />
 */
const PasswordInput = ({
  name,
  value,
  label,
  onChange,
  inputError,
  setInputError,
  isShowChecks,
}: PasswordInputProps) => {
  const { _t } = useTranslator();
  const [focused, setFocused] = useState(false);
  const [allPassed, setAllPassed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const passwordChecks = useMemo(() => {
    const pwd = value || "";
    return {
      length: pwd.length >= 8,
      upper: /[A-Z]/.test(pwd),
      lower: /[a-z]/.test(pwd),
      number: /\d/.test(pwd),
      special: /[!@#$%^&*(),.?":{}|<>_]/.test(pwd),
    };
  }, [value]);

  useEffect(() => {
    const passed = Object.values(passwordChecks).every(Boolean);

    if (passed && focused) {
      setAllPassed(true);
      setFocused(false);
    } else if (!passed && allPassed) {
      setAllPassed(false);

      const activeElement = document.activeElement as HTMLInputElement | null;
      if (activeElement?.name === name) {
        setFocused(true);
      }
    }
  }, [passwordChecks, focused, allPassed, name]);

  const renderCheck = (condition: boolean, text: string) => (
    <li className={condition ? "is-passed" : ""}>
      <span aria-hidden="true">{condition ? "✓" : "•"}</span>
      {text}
    </li>
  );

  return (
    <>
      <TextField
        id={`password-${name}`}
        name={name}
        icon={<Icon name="lock" />}
        label={label ?? _t("password")}
        type={showPassword ? "text" : "password"}
        placeholder={_t("enterPassword", "Text used in onboarding process")}
        value={value}
        error={name === "confirmPassword" && !!inputError}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          onChange(e);
          if (inputError) {
            setInputError?.("");
          }
        }}
        required
        trailing={
          <button
            type="button"
            className="ds-password-eye"
            aria-label={label ?? _t("password")}
            aria-pressed={showPassword}
            onClick={() => setShowPassword((prev) => !prev)}
          >
            {showPassword ? <VscEye /> : <VscEyeClosed />}
          </button>
        }
      />
      {focused && isShowChecks && (
        <ul className="ds-password-rules">
          {renderCheck(passwordChecks.upper, _t("atLeastOneUpperCaseLetter"))}
          {renderCheck(passwordChecks.lower, _t("atLeastOneLowerCaseLetter"))}
          {renderCheck(passwordChecks.number, _t("atLeastOneNumber"))}
          {renderCheck(passwordChecks.special, _t("atLeastOneSpecialLetter"))}
          {renderCheck(passwordChecks.length, _t("minimumEightCharactersLong"))}
        </ul>
      )}
    </>
  );
};

export default PasswordInput;
